import { useEffect, useLayoutEffect, useState } from "react";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, } from "@/components/ui/dialog";
import useSolanaTransaction from "@/callbacks/useSolanaTransaction";
import { useSolanaWallets } from "@privy-io/react-auth";
import { useSnackbar } from "@/components/Common/Snackbar/SnackbarContext";
import { Loader } from "@/assets/Loader";
import { BuyStepsEnum } from "@/types/enums/StepsEnum";
import ArrowLeft from "@/assetsV3/Wallet/ArrowLeft";
import WalletSelectionSheet from "../../BottomSheets/WalletSelectionSheet";
import GccSelectionSheet from "../../BottomSheets/GccSelectionSheet";
import { IGCCHoldings } from "@/types/IGCCHoldings";
import { SendTransactionError } from "@solana/web3.js";
import { handleStepChange } from "@/utils/marketplace/stepsHandler";
import useSize from "@/hooks/useSize";
import { WebOfferListForm } from "../WebOfferListForm";
import { ReviewStep } from "./ReviewStep";
import { ListSuccessStep } from "./ListSuccessStep";
import { useAppSelector } from "@/redux/hooks";
import DetailStep from "./DetailStep";
import LoaderSvg from "@/components/Auth/Common/Loader";
import { ListFailStep } from "./ListFailStep";

type CreateOfferProps = {
  clickable: () => JSX.Element;
  listSteps: BuyStepsEnum;
  setListSteps: (val: BuyStepsEnum) => void;
  holding: IGCCHoldings | undefined;
  isEnableWalletSelection: boolean;
};

export function CreateOffer({
  clickable,
  listSteps,
  setListSteps,
  holding,
  isEnableWalletSelection,
}: CreateOfferProps) {
  const { fromLamports } = useSolanaTransaction();
  const windowSize = useSize();
  const _currentWallet = useAppSelector((state) => state.wallet);
  const [gasFee, setGasFee] = useState<number>(0);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [solBalance, setSolBalance] = useState<number>(0);
  const [loader, setLoader] = useState<boolean>(false);
  const { wallets } = useSolanaWallets();
  const { openSnackbar } = useSnackbar();
  const {
    getSolBalance,
    getGCCTokenBalanceBySubject,
    createMarketplaceOffer,
    broadcastTransaction,
    getBlockchainGasFee,
  } = useSolanaTransaction();
  const [displayAmount, setDisplayAmount] = useState(""); // State to track the displayed value
  const [displayPrice, setDisplayPrice] = useState(""); // State to track the displayed value
  const [selection, setSelection] = useState(0); //0 for amout 1 for price
  const [selectedWallet, setSelectedWallet] = useState<string>("");
  const [bottomSheetState, setBottomSheetState] = useState(false);
  const [gasFeeLoader, setGasFeeLoader] = useState<boolean>(false);
  const handleBottomSheetChange = (isOpen: boolean) => {
    setBottomSheetState(isOpen);
  };
  const [gccSheetState, setGccSheetState] = useState(false);
  const handleGccSheetChange = (isOpen: boolean) => {
    setGccSheetState(isOpen);
  };
  const [selectedGcc, setSelectedGcc] = useState<IGCCHoldings>();

  useEffect(() => {
    if (holding) {
      setSelectedGcc(holding);
    }
  }, [holding]);

  useEffect(() => {
    if (showModal) {
      calculateGasFee();
    }
  }, [showModal, selectedGcc])

  useEffect(() => {
    if (wallets && selectedWallet && showModal) {
      const init = async () => {
        await loadMore();
      };
      init();
    }
  }, [selectedWallet, showModal]);

  const [isMobile, setIsMobile] = useState<boolean>(false);
  useLayoutEffect(() => {
    if (windowSize.width < 680) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowSize.width]);

  const loadMore = async () => {
    const balance = await getSolBalance();
    setSolBalance(balance ? balance : 0);
  };

  // Gas Fee Calculation
  const calculateGasFee = async () => {
    if (selectedGcc) {
      setGasFeeLoader(true);
      const transaction = await createMarketplaceOffer(
        selectedGcc.subjectAddress,
        +displayPrice,
        +displayAmount
      );
      if (transaction) {
        const gasFeeValue = await getBlockchainGasFee(transaction, selectedGcc.subjectAddress);
        if (gasFeeValue) {
          setGasFee(gasFeeValue);
        }
      }
      setGasFeeLoader(false);
    }
  }
  // Gas Fee Calculation End

  const list = async () => {
    if (listSteps === BuyStepsEnum.FIVE || listSteps === BuyStepsEnum.FOUR) {
      setShowModal(false);
      setListSteps(BuyStepsEnum.ONE);
    } else {
      if (+displayAmount > 0 && +displayPrice > 0 && selectedGcc) {
        if (isMobile) {
          setListSteps(BuyStepsEnum.THREE)
        } else {
          setLoader(true);
        }
        try {
          const transaction = await createMarketplaceOffer(
            selectedGcc.subjectAddress,
            +(+displayPrice).toFixed(8),
            +displayAmount
          );
          if (transaction) {
            const transactionHash = await broadcastTransaction(transaction, "CreateOffer");
            console.log(transaction);
            if (transactionHash && transactionHash.status === "confirmed") {
              if (isMobile) {
                setListSteps(BuyStepsEnum.FOUR);
              } else {
                openSnackbar({
                  message: `${selectedGcc?.subject.displayName} Chip Listed Successfully`,
                  severity: "success",
                });
                setShowModal(false);
                clear();
              }
              await loadMore();
            } else {
              if (isMobile)
                setListSteps(BuyStepsEnum.FIVE);
              else
                openSnackbar({ message: "Failed to List", severity: "error" });
            }
          }
        } catch (error) {
          if (error instanceof SendTransactionError) {
            console.error(error);

            if (error?.stack?.includes("Marketplace has not been initialized")) {
              if (isMobile)
                setListSteps(BuyStepsEnum.FIVE);
              else
                openSnackbar({
                  message: "Marketplace has not been initialized for this token.",
                  severity: "error",
                });
            } else if (error?.stack?.includes("Invalid token amount!")) {
              if (isMobile)
                setListSteps(BuyStepsEnum.FIVE);
              else
                openSnackbar({
                  message: "Token already listed",
                  severity: "error",
                });
            } else if (error?.stack?.includes("already in use")) {
              if (isMobile)
                setListSteps(BuyStepsEnum.FIVE);
              else
                openSnackbar({
                  message: "Please adjust existing listing to list Chips for the same price",
                  severity: "error",
                });
            } else {
              if (isMobile)
                setListSteps(BuyStepsEnum.FIVE);
              else
                openSnackbar({
                  message: error.transactionError.message ?? "Failed to List",
                  severity: "error",
                });
            }
          } else {
            if (isMobile)
              setListSteps(BuyStepsEnum.FIVE);
            else
              openSnackbar({
                message: "Failed to List",
                severity: "error",
              });
          }
        } finally {
          setLoader(false);
        }
      } else {
        if (+displayPrice === 0) {
          openSnackbar({ message: "Price is required", severity: "error" });
        }
        if (+displayAmount === 0) {
          openSnackbar({ message: "Amount is required", severity: "error" });
        }
      }
    }
  };

  const clear = () => {
    setDisplayAmount("");
    setDisplayPrice("");
    setListSteps(BuyStepsEnum.ONE);
  };
  const remainingAmount = selectedGcc
    ? fromLamports(selectedGcc.tokens) -
    fromLamports(selectedGcc.tokens_onmarket)
    : 0;

  const isButtonDisabled = () => {
    if (isMobile) {
      if (!isEnableWalletSelection && remainingAmount === 0) {
        return true;
      } else {
        switch (listSteps) {
          case BuyStepsEnum.ONE:
            return !selectedWallet || !selectedGcc || +displayAmount === 0 || +displayPrice === 0 || remainingAmount === 0 || gasFee > solBalance;
          case BuyStepsEnum.TWO:
            return !selectedGcc;
          case BuyStepsEnum.THREE:
            return (
              +displayAmount === 0 ||
              +displayPrice === 0 ||
              remainingAmount === 0 ||
              gasFee > solBalance
            );
          default:
            return false; // Allow the button to be enabled for other steps
        }
      }
    } else {
      return (
        +displayAmount === 0 ||
        +displayPrice === 0 ||
        !selectedGcc ||
        selectedWallet === "" ||
        remainingAmount === 0 ||
        gasFee > solBalance
      );
    }
  };

  useEffect(() => {
    if (
      isMobile &&
      !isEnableWalletSelection &&
      listSteps === BuyStepsEnum.ONE
    ) {
      setSelectedWallet(_currentWallet.address);
    }
  }, [isEnableWalletSelection, listSteps, isMobile]);

  return (
    <>
      <div
        className="flex"
        onClick={() => {
          setShowModal(true);
          setListSteps(BuyStepsEnum.ONE);
        }}
      >
        {clickable && clickable()}
      </div>
      <Dialog open={showModal}>
        <DialogContent
          className={`flex font-inter sm:mt-5 flex-col ${listSteps === BuyStepsEnum.THREE
            ? "justify-start"
            : "justify-between"
            } gap-0 bg-outbackground-main z-[1000] rounded-none outmd:rounded-3xl outmd:max-w-[400px] outmd:w-[1343px] w-full outmd:h-fit h-full`}
        >
          <div
            className={`flex flex-col ${listSteps !== BuyStepsEnum.ONE ? "h-full" : ""
              }`}
          >
            {!loader && listSteps !== BuyStepsEnum.THREE && listSteps !== BuyStepsEnum.FOUR &&
              <DialogHeader>
                <div className="flex flex-col w-full outmd:hidden">
                  <div className="flex reletive gap-4 h-[72px] justify-center items-center px-5 py-4 w-full">
                    <DialogClose
                      asChild
                      onClick={() => {
                        handleStepChange("down", listSteps, setListSteps, () => {
                          setShowModal(false);
                          clear();
                        });
                      }}
                    >
                      <div className="absolute left-[20px] top-7 flex flex-col w-6 h-6 rounded-lg cursor-pointer">
                        <ArrowLeft className="fill-outicon-sub h-full w-full" />
                      </div>
                    </DialogClose>

                    <div className="flex items-center gap-4">
                      <p className="text-base text-outext-main font-bold font-inter ">
                        List
                      </p>
                    </div>
                  </div>
                </div>
              </DialogHeader>}
            {/* Mobile Steps here  */}
            <div className="flex flex-col h-full outmd:hidden">
              {listSteps === BuyStepsEnum.ONE ? (
                <DetailStep
                  selectedWallet={selectedWallet}
                  setSelectedWallet={setSelectedWallet}
                  setSelectedGcc={setSelectedGcc}
                  selectedGcc={selectedGcc}
                  setShowModal={setShowModal}
                  displayAmount={displayAmount}
                  setDisplayAmount={setDisplayAmount}
                  displayPrice={displayPrice}
                  setDisplayPrice={setDisplayPrice}
                  isEnableWalletSelection={isEnableWalletSelection}
                />
              ) : listSteps === BuyStepsEnum.TWO ? (
                <ReviewStep
                  selectedGcc={selectedGcc}
                  displayAmount={displayAmount}
                  displayPrice={displayPrice}
                  gasFee={gasFee}
                />
              ) : listSteps === BuyStepsEnum.THREE ? (
                <div className="flex justify-center items-center h-full">
                  <LoaderSvg />
                </div>
              ) : listSteps === BuyStepsEnum.FOUR ? (
                <ListSuccessStep />
              ) : listSteps === BuyStepsEnum.FIVE ? (
                <ListFailStep />
              ) : <></>}
            </div>
            {/* Web view here */}
            <WebOfferListForm
              selectedWallet={selectedWallet}
              setSelectedWallet={setSelectedWallet}
              setSelectedGcc={setSelectedGcc}
              selectedGcc={selectedGcc}
              setShowModal={setShowModal}
              displayAmount={displayAmount}
              setDisplayAmount={setDisplayAmount}
              displayPrice={displayPrice}
              setDisplayPrice={setDisplayPrice}
              isEnableWalletSelection={isEnableWalletSelection}
              gasFee={gasFee}
              solBalance={solBalance}
              gasFeeLoader={gasFeeLoader}
            />
          </div>
          <DialogFooter>
            <div className="p-6 pb-[30px] outmd:pt-0 w-full">
              {
                listSteps === BuyStepsEnum.THREE
                  ?
                  <></>
                  :
                  <button
                    disabled={isButtonDisabled()}
                    type="button"
                    onClick={() => {
                      if (listSteps === BuyStepsEnum.FOUR || !isMobile) {
                        list();
                      } else {
                        if (listSteps === BuyStepsEnum.ONE) {
                          setListSteps(BuyStepsEnum.TWO);
                        } else if (listSteps === BuyStepsEnum.TWO) {
                          list();
                        } else {
                          setShowModal(false);
                          setListSteps(BuyStepsEnum.ONE);
                        }
                      }
                    }}
                    className="flex w-full flex-1 justify-center text-outstate-blackcompact text-base font-bold items-center h-[62px] p-2 bg-outstate-whitecompact rounded-[100px] aspect-square disabled:bg-outstate-grayLight disabled:cursor-default disabled:text-outext-sub"
                  >
                    {loader ? (
                      <Loader className="w-10 h-10 fill-outext-opposite" />
                    ) : remainingAmount === 0 ? (
                      isMobile ? "Next" : "List Chips"
                    ) : !isMobile ? (
                      "List Chip"
                    ) : listSteps === BuyStepsEnum.FOUR || listSteps === BuyStepsEnum.FIVE ? (
                      "Done"
                    ) : (
                      listSteps === BuyStepsEnum.ONE ? "Next" :
                        "Confirm"
                    )}
                  </button>
              }

              <WalletSelectionSheet
                selectedWallet={selectedWallet}
                setSelectedWallet={setSelectedWallet}
                bottomSheetState={bottomSheetState}
                handleSheetChange={handleBottomSheetChange}
              />
              <GccSelectionSheet
                handleGccSheetChange={handleGccSheetChange}
                selectedGcc={selectedGcc}
                setSelectedGcc={setSelectedGcc}
                gccSheetState={gccSheetState}
              />
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
